import { useEffect, useState } from 'react';
import { date, money } from '../lib/format';
import { errorMessage, messageForCode } from '../lib/api-error';
import {
  GRID_LABELS,
  downloadClientListingXml,
  downloadIcListingXml,
  downloadVatReturnXml,
  IC_CODE_LABELS,
  useClientListing,
  useIcListing,
  useVatPeriods,
  useVatReturnPreview,
  type DeclarationProblem,
} from '../lib/vat-return-api';
import { saveBlob } from '../lib/einvoice-api';
import { VatDeclarationRecords } from './VatDeclarationRecords';
import { VatAdjustments } from './VatAdjustments';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  CardHeader,
  EmptyState,
  ErrorState,
  Select,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * PERİYODİK KDV BEYANI — önizleme.
 *
 * ⚠ "GÖNDER" DÜĞMESİ YOK ve bu bir eksiklik değil, dürüstlüktür. Beyanın
 * kaydedilmesi (arşivlenen XML, gönderim durumu, imzalı makbuz) henüz
 * yazılmadı; düğme koymak operatörün beyanı VERDİĞİNİ sanması demektir.
 * Bugün bu ekranın işi kutuları göstermek ve tutarsızlıkları söylemektir.
 *
 * ⚠ KUTULAR ADIYLA GÖSTERİLİR. Muhasebecisi olmayan bir operatör "kutu 88"in
 * ne olduğunu bilmez; yanlış bir rakamı fark etmenin tek yolu kutunun ne
 * ölçtüğünü okumaktır.
 */

/** Bugünün çeyreği — panelin varsayılanı. */
function currentQuarterKey(): string {
  const now = new Date();
  return `${now.getFullYear()}Q${Math.ceil((now.getMonth() + 1) / 3)}`;
}

/**
 * Sorun kodunun operatör diline çevrilmesi.
 *
 * ⚠ TEK SÖZLÜK. Bu sayfa bir süre KENDİ metin haritalarını taşıyordu
 * (`PROBLEM_TEXT`, `IC_PROBLEM_TEXT`, `CLIENT_LISTING_PROBLEM_TEXT`) ve merkezî
 * sözlük o kodların HİÇBİRİNİ tanımıyordu. Ekranda metin göründüğü için arıza
 * görünmüyordu; ama aynı kod bir hata NESNESİ içinde geldiğinde (XML indirme
 * reddi gibi) operatör genel "beklenmeyen hata" cümlesini görüyordu — yani
 * panel aynı kuralı iki ayrı şekilde anlatıyordu. Metinler merkezî sözlüğe
 * taşındı ve sürüklenme testi artık beyan kaynaklarını da tarıyor.
 */
function aciklama(code: string): string {
  return messageForCode(code) ?? 'Açıklama bulunamadı.';
}

export function VatReturnPage() {
  /*
   * ⚠ İLK DEĞER YER TUTUCUDUR: gerçek varsayılan sunucudan gelen "beyanı
   * verilecek dönem"dir ve aşağıdaki efekt onu yerleştirir. Panelde
   * hesaplamak, aylık mükellefe çeyrek anahtarı vermek demekti.
   */
  const periods = useVatPeriods();
  const [period, setPeriod] = useState(currentQuarterKey());
  const [periodTouched, setPeriodTouched] = useState(false);

  useEffect(() => {
    if (!periodTouched && periods.data) setPeriod(periods.data.due);
  }, [periods.data, periodTouched]);
  const [clientListingNihil, setClientListingNihil] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const preview = useVatReturnPreview(period);
  const icListing = useIcListing(period);
  const [icError, setIcError] = useState<string | null>(null);
  const [icDownloading, setIcDownloading] = useState(false);
  /*
   * ⚠ 725 YILLIKTIR, dönemli değil — şemadaki `Period` yalnız yıl taşıyor.
   * Yıl, seçili dönemin ilk dört hanesinden alınıyor.
   */
  const yil = Number(period.slice(0, 4));
  const clientListing = useClientListing(Number.isInteger(yil) ? yil : null);
  const [clError, setClError] = useState<string | null>(null);
  const [clDownloading, setClDownloading] = useState(false);

  const blockers = (preview.data?.declaration.problems ?? []).filter((p) => p.level === 'BLOCKER');
  const warnings = (preview.data?.declaration.problems ?? []).filter((p) => p.level === 'WARNING');

  return (
    <>
      <PageHeader
        title="KDV beyanı"
        description="Dönemin kutuları, belgelerin DONDURDUĞU değerlerden toplanır — hiçbiri burada yeniden hesaplanmaz."
      />

      <Card className="mt-4">
        <div className="flex flex-wrap items-end gap-3 px-4 py-3">
          {/*
            ⚠⚠ DÖNEM ARTIK SUNUCUDAN GELİYOR VE VARSAYILAN "BEYANI VERİLECEK"
            OLANDIR. Ekran bugünün çeyreğini açıyordu: operatör bu sayfayı
            Nisan/Temmuz/Ekim/Ocak'ta açar — yani hep bir sonraki dönemin
            içindeyken — ve karşısına neredeyse boş bir dönem gelirdi.
            Serbest metin kutusu da gitti: yarım yazılmış bir anahtar sonsuz
            "yükleniyor" gösteriyordu ve aylık mükellefe çeyrek teklif
            ediliyordu.
          */}
          <Select
            label="Dönem"
            value={period}
            hint={
              periods.data
                ? `${periods.data.periodicity === 'MONTHLY' ? 'Aylık' : 'Çeyreklik'} mükellef · ${period === periods.data.due ? 'beyanı verilecek dönem' : period === periods.data.current ? '⚠ bu dönem HENÜZ BİTMEDİ' : 'geçmiş dönem'}`
                : 'Yükleniyor…'
            }
            onChange={(e) => {
              setPeriodTouched(true);
              setPeriod(e.target.value);
            }}
          >
            {(periods.data?.items ?? []).map((p) => (
              <option key={p.key} value={p.key}>
                {p.key}
                {p.key === periods.data?.due ? ' — beyanı verilecek' : ''}
                {!p.ended ? ' (bitmedi)' : ''}
                {p.declarationStatus === 'SUBMITTED'
                  ? ' ✓ gönderildi'
                  : p.declarationStatus === 'PREPARED'
                    ? ' · hazırlandı, GÖNDERİLMEDİ'
                    : ''}
              </option>
            ))}
            {periods.data === undefined && <option value={period}>{period}</option>}
          </Select>
          {preview.data && (
            <div className="text-xs text-[var(--c-ink-soft)]">
              <p>
                {date(preview.data.period.startDate)} – {date(preview.data.period.endDate)}
              </p>
              <p>
                Son gün <strong>{date(preview.data.period.dueDate)}</strong>
              </p>
              <p>
                {preview.data.documentCounts.sales} satış · {preview.data.documentCounts.purchases}{' '}
                alış belgesi
              </p>
            </div>
          )}
        </div>
      </Card>

      {/*
        ⚠ BLOKE EDİCİLER EN ÜSTTE VE KIRMIZI. Bunlar "dosya reddedilir"
        anlamına gelir; sayfanın altına gömülmüş bir rozet, operatörün
        göremeyeceği tek yerdir.
      */}
      {blockers.length > 0 && (
        <Card className="mt-4">
          <CardHeader
            title="Beyan GÖNDERİLEMEZ"
            description="Aşağıdakiler düzeltilmeden dosya Intervat tarafından reddedilir."
          />
          <div className="space-y-3 px-4 pb-4">
            {blockers.map((p, i) => (
              <ProblemRow key={i} problem={p} tone="danger" />
            ))}
          </div>
        </Card>
      )}

      {warnings.length > 0 && (
        <Card className="mt-4">
          <CardHeader
            title="Dikkat"
            description="Beyan kendi içinde geçerli, ama başka bir yükümlülüğe işaret ediyor."
          />
          <div className="space-y-3 px-4 pb-4">
            {warnings.map((p, i) => (
              <ProblemRow key={i} problem={p} tone="warn" />
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-4">
        <CardHeader title="Kutular" />
        {preview.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : preview.isError ? (
          <ErrorState message={errorMessage(preview.error)} />
        ) : !preview.data ? (
          <EmptyState title="Dönem seçin" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Kutu</Th>
                  <Th>Ne ölçüyor</Th>
                  <Th align="right">Tutar</Th>
                </tr>
              </thead>
              <tbody>
                {preview.data.declaration.grids.length === 0 ? (
                  <tr>
                    <Td colSpan={3}>
                      <span className="text-xs text-[var(--c-ink-soft)]">
                        Bu dönemde hiçbir kutu dolmadı — nihil beyan.
                      </span>
                    </Td>
                  </tr>
                ) : (
                  preview.data.declaration.grids.map((g) => (
                    <tr key={g.grid}>
                      <Td className="font-mono text-xs">{g.grid}</Td>
                      <Td>{GRID_LABELS[g.grid] ?? '—'}</Td>
                      <Td align="right">{money(g.amount)}</Td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            <div className="border-t border-[var(--c-border)] px-4 py-3">
              <dl className="grid gap-1 text-sm sm:grid-cols-2">
                <div className="flex justify-between gap-4">
                  <dt className="text-[var(--c-ink-soft)]">Borç toplamı (54+55+56+57+61+63)</dt>
                  <dd>{money(preview.data.declaration.totalDue)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[var(--c-ink-soft)]">Alacak toplamı (59+62+64)</dt>
                  <dd>{money(preview.data.declaration.totalDeductible)}</dd>
                </div>
              </dl>
              {/*
                ⚠ 71 VE 72'DEN YALNIZ BİRİ DOLAR. Nihil beyanda bile biri
                yazılır (0,00): Intervat ikisi de boş olan bir beyanı reddeder.
              */}
              <p className="mt-3 text-lg font-semibold">
                {preview.data.declaration.grid71 !== null ? (
                  <>Kutu 71 — ödenecek: {money(preview.data.declaration.grid71)}</>
                ) : (
                  <>Kutu 72 — iade alınacak: {money(preview.data.declaration.grid72 ?? '0.00')}</>
                )}
              </p>
              <p className="mt-1">
                <Badge tone={preview.data.submittable ? 'ok' : 'danger'}>
                  {preview.data.submittable ? 'gönderilebilir' : 'GÖNDERİLEMEZ'}
                </Badge>
              </p>
            </div>
          </>
        )}
      </Card>

      {/*
        ⚠ EKSİK OLANI SÖYLEMEK, DÜĞMEYLE VAAT ETMEKTEN İYİDİR. Operatör beyanı
        Intervat'a nasıl vereceğini bilmeli; yazılımın henüz yapmadığı şeyi
        yaptığını sanmamalı.
      */}
      {/*
        ⚠ IC LISTING AYRI BİR BEYANDIR ve kendi süresi vardır. Periyodik
        beyanda kutu 44/46 doluysa bu liste de verilmek zorunda; periyodik
        beyan onsuz da geçerlidir (orada UYARI, bloke edici değil).
      */}
      {(icListing.data?.listing.clients.length ?? 0) > 0 ||
      (icListing.data?.listing.problems.length ?? 0) > 0 ? (
        <Card className="mt-4">
          <CardHeader
            title="AB içi işlemler listesi (723)"
            description="Müşteri bazında toplanır: aynı müşteriye kesilen faturalar TEK satır olur."
          />

          {icListing.data?.listing.problems.map((pr, i) => (
            <div
              key={i}
              className="mx-4 mb-2 rounded-[var(--r-md)] border border-[var(--c-danger)] px-3 py-2"
            >
              <p className="text-sm font-medium text-[var(--c-danger)]">
                {pr.code}
                {pr.documentNumber ? ` · ${pr.documentNumber}` : ''}
              </p>
              <p className="text-xs text-[var(--c-ink-soft)]">{aciklama(pr.code)}</p>
            </div>
          ))}

          <Table>
            <thead>
              <tr>
                <Th>Ülke</Th>
                <Th>KDV numarası</Th>
                <Th>Kod</Th>
                <Th align="right">Tutar</Th>
              </tr>
            </thead>
            <tbody>
              {(icListing.data?.listing.clients ?? []).map((c) => (
                <tr key={`${c.countryCode}${c.vatNumber}${c.code}`}>
                  <Td>{c.countryCode}</Td>
                  {/*
                    ⚠ NUMARA ÖN EKSİZ GÖSTERİLİYOR, çünkü dosyada da öyle:
                    ülke ayrı bir öznitelikte (`issuedBy`). Ön ekli yazmak
                    dosyayı reddettirir.
                  */}
                  <Td className="font-mono text-xs">{c.vatNumber}</Td>
                  <Td>
                    <Badge tone="brand">{c.code}</Badge>
                    <span className="ml-2 text-xs text-[var(--c-ink-soft)]">
                      {IC_CODE_LABELS[c.code] ?? ''}
                    </span>
                  </Td>
                  <Td align="right">{money(c.amount)}</Td>
                </tr>
              ))}
              <tr>
                <Td> </Td>
                <Td>
                  <span className="font-semibold">
                    {icListing.data?.listing.clientsNbr ?? 0} müşteri
                  </span>
                </Td>
                <Td> </Td>
                <Td align="right">
                  <span className="font-semibold">
                    {money(icListing.data?.listing.amountSum ?? '0.00')}
                  </span>
                </Td>
              </tr>
            </tbody>
          </Table>

          <div className="flex items-center gap-2 border-t border-[var(--c-border)] px-4 py-3">
            <Button
              variant="secondary"
              disabled={icDownloading || icListing.data?.submittable !== true}
              onClick={() => {
                setIcError(null);
                setIcDownloading(true);
                downloadIcListingXml(period)
                  .then((f) => saveBlob(f.blob, f.fileName))
                  .catch((e: unknown) => setIcError(errorMessage(e)))
                  .finally(() => setIcDownloading(false));
              }}
            >
              723 XML indir
            </Button>
            {icError && <span className="text-xs text-[var(--c-danger)]">{icError}</span>}
          </div>
        </Card>
      ) : null}

      {/*
        ⚠ 725 AYRI BİR BEYAN VE YILLIKTIR. Periyodik beyandaki
        `ClientListingNihil` bayrağı bu listeyle aynı şey DEĞİL: o, "listem
        nihil" beyanıdır; bu ise listenin kendisi.
      */}
      <Card className="mt-4">
        <CardHeader
          title={`Yıllık müşteri listesi (725) — ${yil}`}
          description="Belçikalı KDV mükellefi müşteriler, takvim yılı boyunca. Müşteri bazında toplanır."
        />

        {clientListing.data?.listing.problems.map((pr, i) => (
          <div
            key={i}
            className="mx-4 mb-2 rounded-[var(--r-md)] border border-[var(--c-danger)] px-3 py-2"
          >
            <p className="text-sm font-medium text-[var(--c-danger)]">
              {pr.code}
              {pr.documentNumber ? ` · ${pr.documentNumber}` : ''}
            </p>
            <p className="text-xs text-[var(--c-ink-soft)]">{aciklama(pr.code)}</p>
          </div>
        ))}

        {(clientListing.data?.listing.clients.length ?? 0) === 0 ? (
          <EmptyState
            title="Belçikalı KDV müşterisi yok"
            description="Bu yılda listeye girecek müşteri bulunamadı."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>KDV numarası</Th>
                <Th align="right">Ciro</Th>
                <Th align="right">KDV</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {(clientListing.data?.listing.clients ?? []).map((c) => (
                <tr key={c.vatNumber}>
                  <Td className="font-mono text-xs">{c.vatNumber}</Td>
                  <Td align="right">{money(c.turnOver)}</Td>
                  <Td align="right">{money(c.vatAmount)}</Td>
                  <Td>
                    {/*
                      ⚠ BU BİR SÜZGEÇ DEĞİL, BİLGİ. Hiçbir müşteri otomatik
                      elenmiyor: eşiğin altındakini listeye koymak fazladan
                      bilgidir, üstündekini düşürmek EKSİK BEYANDIR. Üstelik
                      250,00 € rakamının metni doğrulanmadı.
                    */}
                    {c.belowCommonThreshold && <Badge tone="warn">yaygın eşiğin altında</Badge>}
                  </Td>
                </tr>
              ))}
              <tr>
                <Td>
                  <span className="font-semibold">
                    {clientListing.data?.listing.clientsNbr ?? 0} müşteri
                  </span>
                </Td>
                <Td align="right">
                  <span className="font-semibold">
                    {money(clientListing.data?.listing.turnOverSum ?? '0.00')}
                  </span>
                </Td>
                <Td align="right">
                  <span className="font-semibold">
                    {money(clientListing.data?.listing.vatAmountSum ?? '0.00')}
                  </span>
                </Td>
                <Td> </Td>
              </tr>
            </tbody>
          </Table>
        )}

        <div className="space-y-2 border-t border-[var(--c-border)] px-4 py-3">
          <p className="text-xs text-[var(--c-ink-soft)]">
            ⚠ <strong>Hiçbir müşteri otomatik elenmiyor.</strong> Kurallarda bir yıllık ciro eşiği
            olduğu biliniyor (yaygın olarak anılan rakam 250,00 €) ama o rakamın metni bu yazılımda{' '}
            <strong>doğrulanmadı</strong>; eşiğin altındakiler yalnızca işaretleniyor. Göndermeden
            önce kendiniz kontrol edin — eşiğin üstündeki bir müşteriyi düşürmek eksik beyandır.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={clDownloading || clientListing.data?.submittable !== true}
              onClick={() => {
                setClError(null);
                setClDownloading(true);
                downloadClientListingXml(yil)
                  .then((f) => saveBlob(f.blob, f.fileName))
                  .catch((e: unknown) => setClError(errorMessage(e)))
                  .finally(() => setClDownloading(false));
              }}
            >
              725 XML indir
            </Button>
            {clError && <span className="text-xs text-[var(--c-danger)]">{clError}</span>}
          </div>
        </div>
      </Card>

      <Card className="mt-4">
        <CardHeader
          title="Intervat XML"
          description="Dosyayı indirip Intervat portalına yükleyin. Yazılım göndermez — gönderim takibi ve imzalı makbuz arşivi henüz yazılmadı."
        />
        <div className="space-y-3 px-4 pb-4">
          {/*
            ⚠ BU KUTU BİR BEYANDIR, bir tercih değil. "Yıllık müşteri listesi
            nihil" demek, o yıl hiç Belçikalı KDV mükellefi müşterim olmadı
            demektir. Yazılım bunu belgelerden ÇIKARABİLİR gibi görünse de,
            listenin kendisi henüz yazılmadı; çıkarım yapmak, operatörün
            vermediği bir beyanı onun adına vermek olurdu.
          */}
          <Checkbox
            label="Yıllık müşteri listesi (725) NİHİL"
            hint="Bu yıl hiç Belçikalı KDV mükellefi müşteriniz olmadıysa işaretleyin. Emin değilseniz işaretlemeyin."
            checked={clientListingNihil}
            onChange={(e) => setClientListingNihil(e.target.checked)}
          />

          {/*
            ⚠ ŞEMA SÜRÜMÜ UYARISI KALICI OLARAK GÖRÜNÜR. "Üretildi" ile
            "geçerli" aynı şey değil: eleman ağacı v0_7 şemasından okundu,
            v0_9 XSD elimizde yok ve yerel doğrulama yapılamıyor.
          */}
          <p className="rounded-[var(--r-md)] border border-[var(--c-border)] px-3 py-2 text-xs text-[var(--c-ink-soft)]">
            <strong>Dosya yerel olarak doğrulanmadı.</strong> Eleman ağacı Intervat v0_7 şemasından
            okundu; v0_9 XSD elimizde olmadığı için XSD doğrulaması yapılamıyor. Portal yüklerken
            kendi doğrulamasını yapacak — hata verirse beyanı elle girin ve durumu bildirin.
          </p>

          <div className="flex items-center gap-2">
            <Button
              disabled={downloading || preview.data?.submittable !== true}
              onClick={() => {
                setDownloadError(null);
                setDownloading(true);
                downloadVatReturnXml(period, clientListingNihil)
                  .then((f) => saveBlob(f.blob, f.fileName))
                  .catch((e: unknown) => setDownloadError(errorMessage(e)))
                  .finally(() => setDownloading(false));
              }}
            >
              XML indir
            </Button>
            {preview.data?.submittable === false && (
              <span className="text-xs text-[var(--c-danger)]">
                Bloke edici varken dosya üretilmez.
              </span>
            )}
          </div>
          {downloadError && <p className="text-xs text-[var(--c-danger)]">{downloadError}</p>}
        </div>
      </Card>

      {/*
        ⚠ KAYIT KARTI EN ALTTA VE AYRI: yukarısı ÖNİZLEMEDİR (her çağrıda
        yeniden hesaplanır, hiçbir şey saklanmaz), burası ise DONDURULMUŞ
        kayıtlar. İkisini karıştırmak, önizlemeye bakan operatörün beyanını
        verdiğini sanmasına yol açar.
      */}
      <VatAdjustments period={period} />

      <VatDeclarationRecords period={period} year={yil} nihil={clientListingNihil} />
    </>
  );
}

function ProblemRow({ problem, tone }: { problem: DeclarationProblem; tone: 'danger' | 'warn' }) {
  const detay = [
    problem.grid ? `kutu ${problem.grid}` : null,
    problem.amount ? money(problem.amount) : null,
    problem.reverseCharge ? `ters yükümlülük ${money(problem.reverseCharge)}` : null,
    problem.inputBase ? `giriş matrahı ${money(problem.inputBase)}` : null,
    problem.baseGrids ?? null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div>
      <p className="text-sm font-medium">
        <Badge tone={tone}>{problem.code}</Badge>
        {detay && <span className="ml-2 text-[var(--c-ink-soft)]">{detay}</span>}
      </p>
      <p className="mt-1 text-xs text-[var(--c-ink-soft)]">{aciklama(problem.code)}</p>
    </div>
  );
}
